import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Logger, LoggerFactory } from './logger.service';

export const STALE_CHUNK_RELOAD_STATE_KEY = 'triggerbee.staleChunkReload.state';

const RELOAD_COOLDOWN_MS = 30_000;
// Reloads more than this far apart are treated as unrelated incidents (the earlier reload
// succeeded), so the attempt counter resets. Only reloads clustered inside this window count
// toward the loop cap.
const LOOP_WINDOW_MS = 5 * 60_000;
const MAX_RELOADS_PER_LOOP_WINDOW = 3;

// One pattern per engine: Chromium, Firefox, Safari. A failed native import() always rejects
// with a TypeError carrying one of these messages; errors thrown inside a successfully fetched
// module (or by guards/resolvers) carry their own message and will not match.
const STALE_CHUNK_MESSAGE_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
];

const MAIN_SCRIPT_SRC_PATTERN = /<script[^>]*\bsrc="\/?(main[^"]*\.js)"/;

type ReloadState = { count: number; lastAt: number };
type ReloadOutcome =
  'reloading' | 'no-new-deploy' | 'blocked-by-cooldown' | 'blocked-by-loop-cap' | 'skipped-guarded-route';

/* Lazy-route chunks are content-hashed and a redeploy replaces them all, so a tab loaded before
   the deploy imports chunk URLs that no longer exist. That surfaces as a NavigationError wrapping
   a failed dynamic import. Recover by reloading into the attempted URL — but only after confirming
   a new build is actually deployed (so transient network failures and ad-blockers, which produce
   the identical error, never trigger a reload) and only when the reload cannot harm the user. The
   bias throughout is: when anything is uncertain, do nothing and let the error surface as it would
   have without this service. */
@Injectable()
export class StaleChunkReloadService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger: Logger = inject(LoggerFactory).get('StaleChunkReloadService');

  public initialize(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => void this.handleNavigationError(event));
  }

  private async handleNavigationError(event: NavigationError): Promise<void> {
    if (!this.isStaleChunkError(event.error)) {
      return;
    }
    // A hard reload bypasses Angular CanDeactivate guards (the app has no beforeunload backstop),
    // so it could silently discard unsaved work. The chunk fails during route recognition, before
    // CanDeactivate runs, so the user is still safely on the guarded page — leave them there.
    if (this.currentRouteHasDeactivationGuard()) {
      this.logOutcome('skipped-guarded-route', event);
      return;
    }
    const state = this.readReloadState();
    if (state === undefined) {
      // Storage unavailable: we cannot bound a reload loop, so do not reload at all.
      return;
    }
    if (this.isInReloadCooldown(state)) {
      this.logOutcome('blocked-by-cooldown', event);
      return;
    }
    const attemptsInWindow = this.attemptsInCurrentWindow(state);
    if (attemptsInWindow >= MAX_RELOADS_PER_LOOP_WINDOW) {
      this.logOutcome('blocked-by-loop-cap', event);
      return;
    }
    if (!(await this.newDeployAvailable())) {
      this.logOutcome('no-new-deploy', event);
      return;
    }
    if (!this.recordReloadAttempt(attemptsInWindow)) {
      // Could not persist the attempt, so we could not bound a loop — fail safe, do not reload.
      return;
    }
    this.logOutcome('reloading', event);
    this.reloadTo(event.url);
  }

  private isStaleChunkError(error: unknown): boolean {
    return error instanceof TypeError && STALE_CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(error.message));
  }

  // The page the user sits on stays active because the failed navigation was aborted. If any of
  // its active routes opted into a CanDeactivate guard, the app considers it capable of holding
  // unsaved work; reloading is unsafe. Anything we cannot read is treated as guarded (safe default).
  private currentRouteHasDeactivationGuard(): boolean {
    const root = this.router.routerState?.snapshot?.root;
    if (root === undefined || root === null) {
      return true;
    }
    const pending: ActivatedRouteSnapshot[] = [root];
    while (pending.length > 0) {
      const node = pending.pop();
      if (node === undefined) {
        continue;
      }
      if ((node.routeConfig?.canDeactivate?.length ?? 0) > 0) {
        return true;
      }
      pending.push(...node.children);
    }
    return false;
  }

  private isInReloadCooldown(state: ReloadState): boolean {
    return state.lastAt > 0 && Date.now() - state.lastAt < RELOAD_COOLDOWN_MS;
  }

  private attemptsInCurrentWindow(state: ReloadState): number {
    const sinceLast = Date.now() - state.lastAt;
    return sinceLast > LOOP_WINDOW_MS ? 0 : state.count;
  }

  private async newDeployAvailable(): Promise<boolean> {
    const html = await this.fetchDeployedIndexHtml();
    if (html === undefined) {
      return false;
    }
    const deployedMainSrc = MAIN_SCRIPT_SRC_PATTERN.exec(html)?.[1];
    const runningMainSrc = this.runningMainScriptSrc();
    return deployedMainSrc !== undefined && runningMainSrc !== undefined && deployedMainSrc !== runningMainSrc;
  }

  // /index.html is served with Cache-Control: no-store (staticwebapp.config.json), so this
  // always sees the currently deployed build — deep-link paths do not get those headers.
  private async fetchDeployedIndexHtml(): Promise<string | undefined> {
    try {
      const response = await fetch('/index.html', { cache: 'no-store' });
      return response.ok ? await response.text() : undefined;
    } catch {
      return undefined;
    }
  }

  // The running document still holds the script tag from the index.html it was loaded with, which
  // is the pre-deploy baseline. getAttribute (not .src) keeps the bare filename for comparison.
  private runningMainScriptSrc(): string | undefined {
    return Array.from(document.scripts)
      .map((script) => script.getAttribute('src') ?? '')
      .map((src) => (src.startsWith('/') ? src.substring(1) : src))
      .find((src) => /^main[^/]*\.js$/.test(src));
  }

  private readReloadState(): ReloadState | undefined {
    try {
      const raw = sessionStorage.getItem(STALE_CHUNK_RELOAD_STATE_KEY);
      if (raw === null) {
        return { count: 0, lastAt: 0 };
      }
      const parsed = JSON.parse(raw) as Partial<ReloadState>;
      return { count: Number(parsed.count) || 0, lastAt: Number(parsed.lastAt) || 0 };
    } catch {
      return undefined;
    }
  }

  private recordReloadAttempt(attemptsInWindow: number): boolean {
    try {
      const next: ReloadState = { count: attemptsInWindow + 1, lastAt: Date.now() };
      sessionStorage.setItem(STALE_CHUNK_RELOAD_STATE_KEY, JSON.stringify(next));
      return true;
    } catch {
      return false;
    }
  }

  private reloadTo(url: string): void {
    window.location.assign(url);
  }

  private logOutcome(outcome: ReloadOutcome, event: NavigationError): void {
    const errorMessage = event.error instanceof Error ? event.error.message : String(event.error);
    this.logger.warn(`Stale chunk navigation failure (${outcome}) for ${event.url}: ${errorMessage}`);
  }
}
